'use client';

import { FC } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import FadeIn from '@/components/FadeIn/FadeIn';
import { ComparisonData } from '../types';
import { usePostHog } from 'posthog-js/react';

interface CompareHeroProps {
  data: ComparisonData;
}

// Get competitor square icon based on name
const getCompetitorIcon = (slug: string) => {
  if (slug === 'unify') {
    return (
      <Image
        src="/images/compare/unify-square-icon.svg"
        alt="Unify"
        width={32}
        height={32}
        className="w-8 h-8 object-contain"
      />
    );
  }
  if (slug === 'common-room') {
    return (
      <Image
        src="/images/compare/common-square-icon.svg"
        alt="Common Room"
        width={32}
        height={32}
        className="w-8 h-8 object-contain"
      />
    );
  }
  return (
    <div className="w-8 h-8 bg-slate-200 rounded-lg flex items-center justify-center text-xs font-bold text-slate-600">
      ?
    </div>
  );
};

const CompareHero: FC<CompareHeroProps> = ({ data }) => {
  const posthog = usePostHog();

  return (
    <section className="pt-8 pb-12 md:pt-12 md:pb-16">
      <div
        className="container-fluid px-4 md:px-7.5 mx-auto"
        style={{ maxWidth: 'clamp(1366px, 85vw, 1800px)' }}
      >
        <FadeIn variant="down" delay={0.1}>
          <div className="text-center max-w-4xl mx-auto">
            {/* Logo comparison badges */}
            <div className="flex items-center justify-center gap-4 mb-6">
              <Image
                src="/images/compare/mb-square-icon.svg"
                alt="MarketBetter"
                width={56}
                height={56}
                className="w-14 h-14 object-contain"
              />
              <span className="text-slate-400 text-sm font-medium">vs</span>
              <Image
                src={`/images/compare/${data.competitor.slug === 'common-room' ? 'common' : data.competitor.slug}-square-icon.svg`}
                alt={data.competitor.name}
                width={56}
                height={56}
                className="w-14 h-14 object-contain"
              />
            </div>

            {/* Main Title */}
            <h1
              className="font-medium text-black mb-6"
              style={{
                fontSize: 'clamp(1.75rem, 4vw, 3.5rem)',
                lineHeight: '1.2',
              }}
            >
              Compare{' '}
              <span style={{ color: '#094FFE' }}>marketbetter.ai</span> with{' '}
              {data.competitor.name}
            </h1>

            {/* Subtitle */}
            <p
              className="text-gray-600 mb-10 max-w-2xl mx-auto"
              style={{
                fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
                lineHeight: '1.6',
              }}
            >
              {data.hero.subtitle}
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                href="#comparison"
                className="text-slate-600 hover:text-slate-900 font-medium transition-colors"
                style={{
                  fontSize: 'clamp(0.9375rem, 1.25vw, 1.125rem)',
                }}
              >
                See the comparison
              </Link>
              <Link
                href="/book-demo"
                className="group relative font-medium text-white rounded-xl transition-all duration-300 overflow-hidden shadow-lg hover:shadow-xl inline-block"
                onClick={() => posthog?.capture('compare_book_demo_clicked', { competitor: data.competitor.slug })}
                style={{
                  backgroundColor: '#0848E7',
                  padding: 'clamp(0.75rem, 1vw, 1rem) clamp(2rem, 3vw, 3rem)',
                  fontSize: 'clamp(0.9375rem, 1.25vw, 1.125rem)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background =
                    'linear-gradient(to right, #3EA67E, #084FFE)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#0848E7';
                }}
              >
                Book Demo
              </Link>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default CompareHero;
